import { ReactElement, useRef, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import Animated, {
  Easing,
  ReduceMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { Label } from '@/common/components/Label/Label';
import { Stack } from '@/common/components/Stack/Stack';
import { styles } from './Tabs.styles';

interface ITabItemProps {
  label: string;
  sublabel?: string;
  count?: number;
  children: ReactElement | ReactElement[];
}

interface ITabsProps {
  initialTab?: number;
  className?: string;
  children: ReactElement<ITabItemProps> | ReactElement<ITabItemProps>[];
}

export const Tabs = ({ initialTab = 0, className, children }: ITabsProps) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const tabs = Array.isArray(children) ? children : [children];
  const translateX = useSharedValue(0);
  const tabWidths = useRef<number[]>([]).current;

  const handleTabPress = (index: number) => {
    setActiveTab(index);

    const targetX = tabWidths
      .slice(0, index)
      .reduce((acc, width) => acc + width, 0);

    translateX.value = withTiming(targetX, {
      duration: 200,
      easing: Easing.out(Easing.quad),
      reduceMotion: ReduceMotion.System,
    });
  };

  const handleTabLayout = (index: number, width: number) => {
    tabWidths[index] = width;

    if (index === activeTab) {
      const targetX = tabWidths
        .slice(0, index)
        .reduce((acc, width) => acc + width, 0);
      translateX.value = targetX;
    }
  };

  return (
    <View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerClassName="grow"
      >
        <View className={styles.root({ className })} role="tablist">
          {tabs.map((tabItem, index) => (
            <Pressable
              key={index}
              className={styles.tab({ active: activeTab === index })}
              role="tab"
              aria-selected={activeTab === index}
              onLayout={(event) => {
                const tabWidth = event.nativeEvent.layout.width;
                handleTabLayout(index, tabWidth + 32);
              }}
              onPress={() => handleTabPress(index)}
            >
              <Stack horizontal align="center" gap="sm">
                <Label
                  bold
                  color={activeTab === index ? 'neutral30' : 'neutral20'}
                >
                  {tabItem.props.label}
                </Label>
                {tabItem.props.sublabel && (
                  <Label size="sm" color="neutral30">
                    {tabItem.props.sublabel}
                  </Label>
                )}
                {tabItem.props.count && tabItem.props.count > 0 && (
                  <View className={styles.badge()}>
                    <Label color="neutral10" size="sm" bold>
                      {tabItem.props.count}
                    </Label>
                  </View>
                )}
              </Stack>
            </Pressable>
          ))}

          <Animated.View
            className={styles.activeBorder()}
            style={{ transform: [{ translateX }] }}
          />
        </View>
      </ScrollView>
      <View>{tabs[activeTab]}</View>
    </View>
  );
};
